// Lifetime: ba-life's fetal white-matter atlas (lifetime-sim-v0) played through the viewport's `morph` layer, which moves the
// pack's own white-matter surfaces frame by frame under setTime (viewport-api-v0, "Surfaces that change shape"; engines/wgpu
// #743). The scene's slot is app-only: the readers open the run for the clock, the chart and Methods, and this module drives
// the viewport itself, as the 3D flow view does (features/cfd.ts): the sequence into its own slot once the pack is in, the
// layer lit by the map the panel picks, and out again when the scene goes. A viewport without the layer (the 2D preview, an
// older build) is sent nothing, and the panel says so. The model track (LIFE-014's Newton fold) joins here as a second slot
// when ba-life publishes its frames (#2, 10-03). The Parkinson's stages view (LIFE-019, lifetime-regions-v0) is the scene's
// still view on the MNI template: this module opens ba-life's stage maps itself and sends the chosen stage as one `overlay`
// frame, measured or model, a stage an ordinal step and never time; the fetal sequence stands aside while it's on. The scene's
// Period choice (scenarios/index.ts LIFETIME_PERIOD) swaps the main slot's run and its pack: the drive sends whichever sequence the
// slot resolved to, and lights it only by a map that run publishes (the neonatal atlas has none).

import { useEffect } from 'react';
import { create } from 'zustand';
import { useAtlas, type AtlasState } from '../app/store.ts';
import { packOf, stillView } from '../app/scene.ts';
import { LIFETIME_CENTILES, LIFETIME_PD, LIFETIME_THICKNESS, PD_VIEW, THICKNESS_VIEW } from '../scenarios/index.ts';
import { openedSlot } from '../app/sims.ts';
import type { LifetimeHeader, ThicknessSectionsHeader } from '../data/sims/types.ts';
import { defaultSource, SimFiles } from '../data/sims/files.ts';
import { RegionsSim } from '../data/sims/lifetimeRegions.ts';
import { SEXES, ThicknessSim, type ThicknessSex } from '../data/sims/lifetimeThickness.ts';
import { ageYears, CentilesSim, METRIC_NAMES, METRICS, type CentileMetric, type CentileSex } from '../data/sims/lifetimeCentiles.ts';
import type { ChartModel, Series } from '../ui/Chart.tsx';
import { SERIES } from '../ui/scales.ts';
import { drawsLayer } from '../viewport/draws.ts';
import { getViewport } from '../viewport/host.ts';
import { packBoundSlots } from '../viewport/packBound.ts';
import type { Colormap, Command } from '../viewport/api.ts';

export const lifetimeActive = (s: Pick<AtlasState, 'scenario'>): boolean => s.scenario === 'lifetime';

/** Which measured sequence the scene's main slot holds, by its header's age quantity: the fetal atlas (gestational age) or the
 *  neonatal (postmenstrual age), the scene's Period choice (scenarios/index.ts LIFETIME_PERIOD). Fetal until the runs are in. */
export type Period = 'fetal' | 'neonatal';
export const isNeonatal = (h: LifetimeHeader | null | undefined): boolean => h?.age?.quantity === 'postmenstrual_age';
const mainHeader = (s: Pick<AtlasState, 'scenario' | 'sims'>): LifetimeHeader | undefined =>
  s.sims.scenario === s.scenario ? (s.sims.slots.find((x) => x.slot === 'main' && x.format === 'lifetime')?.header as LifetimeHeader | undefined) : undefined;
export const periodOf = (s: Pick<AtlasState, 'scenario' | 'sims'>): Period => (isNeonatal(mainHeader(s)) ? 'neonatal' : 'fetal');
/** The run's age in words: the fetal atlas's gestational weeks, the neonatal atlas's weeks postmenstrual age. */
export const ageWords = (h: LifetimeHeader | null | undefined): string => (isNeonatal(h) ? 'weeks postmenstrual age' : 'gestational weeks');
export const perWeek = (h: LifetimeHeader | null | undefined): string => (isNeonatal(h) ? 'postmenstrual week' : 'gestational week');
/** The same for an axis title, short enough to leave the chart's last ticks uncovered. */
export const ageShort = (h: LifetimeHeader | null | undefined): string => (isNeonatal(h) ? 'weeks PMA' : 'gestational weeks');
/** The sequence the scene's main slot resolved to (the chosen period's run) once its runs are in; null while they load. */
export const sequencePath = (s: Pick<AtlasState, 'scenario' | 'sims'>): string | null =>
  s.sims.scenario === s.scenario && s.sims.status === 'ready' ? (s.sims.slots.find((x) => x.slot === 'main' && x.format === 'lifetime')?.path ?? null) : null;

/** The viewport slot the scene's own drive loads the sequence into. The bridge's sims leave it alone (the scene's slot is app-only);
 *  while a sequence is in it, the bridge empties it before another pack loads, since the sequence belongs to its pack (packBoundSlots). */
export const LIFETIME_SLOT = 'lifetime';

/** What the surface is lit by: nothing (the tissue's own look), or one of the sequence's scalar maps by its published name, over
 *  a fixed scale so a colour means the same at every week (about the 2nd–98th percentiles over weeks 21–36, rounded: sulcal depth
 *  −7.8 to 6.0 mm, curvature −0.22 to 0.27 1/mm, measured on the run on 2026-10-03). */
export interface SurfaceMap {
  id: 'none' | 'depth' | 'curvature';
  name: string;
  short: string;
  /** The map's published name in the run (the layer's `quantity`); null for the plain surface. */
  quantity: string | null;
  units: string;
  range: [number, number] | null;
  colormap: Colormap | null;
  hint: string;
}

export const MAPS: SurfaceMap[] = [
  { id: 'none', name: 'Plain surface', short: 'Plain', quantity: null, units: '', range: null, colormap: null, hint: 'The surface in the tissue’s own look: the folds read from its shape and shading alone.' },
  {
    id: 'depth',
    name: 'Sulcal depth',
    short: 'Depth',
    quantity: 'source_sulcal_depth',
    units: 'mm',
    range: [-8, 8],
    colormap: 'viridis',
    hint: 'How far each point sits below (positive) or above the surface’s smooth hull, the atlas’s own map: the sulci deepen week by week.',
  },
  {
    id: 'curvature',
    name: 'Mean curvature',
    short: 'Curvature',
    quantity: 'source_curvature',
    units: '1/mm',
    range: [-0.25, 0.25],
    colormap: 'coolwarm',
    hint: 'The surface’s mean curvature, the atlas’s own map: warm where it bends outward over a gyrus, cool in the sulcal fundi.',
  },
];
export type MapId = SurfaceMap['id'];
export const DEFAULT_MAP: MapId = 'none';
export const isMap = (v: string | null | undefined): v is MapId => MAPS.some((m) => m.id === v);
export const mapOf = (id: MapId): SurfaceMap => MAPS.find((m) => m.id === id)!;
/** The map the surface is lit by: the panel's, where the run publishes it (the fetal atlas's depth and curvature); plain where it
 *  doesn't (the neonatal atlas publishes none), so no layer asks for a quantity the run lacks. */
export const litMap = (m: SurfaceMap, sim: { maps: { name: string }[] } | null | undefined): SurfaceMap => (m.quantity && sim && !sim.maps.some((x) => x.name === m.quantity) ? mapOf('none') : m);

export type LifetimeStatus = 'idle' | 'loading' | 'on' | 'off' | 'unsupported' | 'error';

/** The track the panel and the strip lead with: the measured atlas (the 3D surface and its area), the model (LIFE-014's section over
 *  the atlas's own section at the same clock), or the assigned thickness (LIFE-020's four sections at an imposed growth of their own,
 *  never the clock). The engine morphs the atlas either way. */
export type Track = 'atlas' | 'model' | 'assigned';
export const DEFAULT_TRACK: Track = 'atlas';
export const isTrack = (v: string | null | undefined): v is Track => v === 'atlas' || v === 'model' || v === 'assigned';

/** Whether the model track's run is among the scene's slots (lifetime-sections-v0, slot `model`). */
export const modelLoaded = (s: Pick<AtlasState, 'sims'>): boolean => s.sims.slots.some((x) => x.slot === 'model' && x.format === 'sections');

/** LIFE-020b's reviewed addendum to the Assigned thickness Methods: the reviewer's wording (#1112 round 2, confirmed on #1127), copied
 *  byte for byte from research/lifetime/life-020b-review/public-wording.json `app_methods_addendum` (461 characters); test/lifetime.test.ts
 *  holds it equal to that file. The sheet shows it after ba-life's own Methods line (Lifetime.tsx AssignedMethods). */
export const LIFE_020B_ADDENDUM = 'A follow-up check (LIFE-020b) reran the 2 mm case from the start on the same section with every triangle split into four: at imposed growth g = 2 its fold wavelength came out 21 % shorter (7.0 mm against 8.9 mm), more than the 10 % allowed, while its overall folding (GI) and stored energy changed by only 3 % and 2 %. So the 2 mm wavelength is not mesh-converged at the resolution shown here; the other three checks were not run, and LIFE-020 stays NOT SCORED.';

/** Whether the assigned-thickness run is among the scene's slots (lifetime-thickness-sections-v0, slot `assigned`). */
export const assignedLoaded = (s: Pick<AtlasState, 'sims'>): boolean => s.sims.slots.some((x) => x.slot === 'assigned' && x.format === 'thickness-sections');
/** The assigned-thickness run's whole-set verdict, copied from its header (survives, killed, NOT SCORED…); null without the run. */
export const assignedVerdict = (s: Pick<AtlasState, 'sims'>): string | null => {
  const h = s.sims.slots.find((x) => x.slot === 'assigned' && x.format === 'thickness-sections')?.header as ThicknessSectionsHeader | undefined;
  if (!h) return null;
  const v = h.source_result?.verdict;
  return typeof v === 'string' && v ? v : 'unknown';
};

/** The caption under the viewport: model · data, the model track named once its run is in, or the assigned-thickness track with its
 *  whole-set verdict while that track leads; the neonatal period's own line. */
export const lifetimeCaption = (model: boolean, period: Period = 'fetal', assigned: string | null = null): string =>
  period === 'neonatal'
    ? 'Derived reference · dHCP neonatal white matter, 37–44 weeks postmenstrual age, both hemispheres in one display frame'
    : `Measured atlas · dHCP fetal white matter, 21–36 gestational weeks${assigned !== null ? ` · model: LIFE-020 assigned thickness, ${assigned}, in section` : model ? ' · model: LIFE-014 Newton fold, in section' : ''}`;

// Assigned thickness (LIFE-020; docs/contracts/lifetime-thickness-sections-v0.md): the strip's four sections at an imposed growth of
// their own. The axis is the model's, never the scene's clock: nothing maps g to the atlas's weeks.

/** The imposed-growth axis the tracks were asked for, g = 1 to 2.6875 in 1/16 steps (the run's own `requested_values` where they
 *  differ); the control moves in quarter steps, so the blend between two saved states can be watched. */
export const GROWTH_RANGE: [number, number] = [1, 2.6875];
export const GROWTH_UI_STEP = 1 / 64;
/** g = 2, the first of the registered test loads (g = 2 and 2.5). */
export const DEFAULT_GROWTH = 2;
/** The thickness shown first, mm: the nearest of the four to the frozen reference's 2.5 mm. */
export const DEFAULT_THICKNESS = 2;
/** On the control's grid inside the axis; the default for a non-number. */
export const clampGrowth = (v: number): number =>
  Number.isFinite(v) ? Math.min(GROWTH_RANGE[1], Math.max(GROWTH_RANGE[0], Math.round(v / GROWTH_UI_STEP) * GROWTH_UI_STEP)) : DEFAULT_GROWTH;
/** Past a track's accepted range: nothing (the contract's rule: no data), or its last accepted state, labelled as such. */
export type Beyond = 'none' | 'last';
export const DEFAULT_BEYOND: Beyond = 'none';
export const isBeyond = (v: string | null | undefined): v is Beyond => v === 'none' || v === 'last';

// Parkinson's stages (LIFE-019; docs/contracts/lifetime-regions-v0.md).

/** Whether the scene's Parkinson's stages view is on. */
export const pdView = (s: Pick<AtlasState, 'scenario'> & Partial<Pick<AtlasState, 'preset'>>): boolean => s.scenario === 'lifetime' && s.preset === PD_VIEW;
/** The track the view shows: the published measured stage maps, or the frozen model's samples. */
export type PdTrack = 'measured' | 'model';
export const DEFAULT_PD_TRACK: PdTrack = 'measured';
export const isPdTrack = (v: string | null | undefined): v is PdTrack => v === 'measured' || v === 'model';
/** The ordinal stage shown, 0–3 (HY1, HY2, HY3, HY4/5): the measured track at the whole stage nearest, the model at the sample
 *  nearest (121 over the range, 0.025 apart). HY3 by default, LIFE-019's registered primary test stage. */
export const DEFAULT_STAGE = 2;
export const STAGE_RANGE: [number, number] = [0, 3];
export const STAGE_STEP = 0.025;
export const clampStage = (v: number): number => (Number.isFinite(v) ? Number(Math.min(STAGE_RANGE[1], Math.max(STAGE_RANGE[0], Math.round(v / STAGE_STEP) * STAGE_STEP)).toFixed(3)) : DEFAULT_STAGE);
export type PdStatus = 'idle' | 'loading' | 'on' | 'error';
/** The overlay's id in the viewport. */
export const PD_OVERLAY = 'lifetime:pd';
/** The caption under the viewport in the view: model · data, the verdict named. */
export const pdCaption = (): string => 'Measured (ENIGMA-PD) · cortical thinning by Hoehn and Yahr stage · model: connectome diffusion, LIFE-019 killed';
/** The sheet's paragraph for the view. */
export const PD_METHODS = 'Cortical thinning by Parkinson’s stage on the MNI template’s cortex: ENIGMA-PD’s measured group maps at four Hoehn and Yahr stages (patients against controls, signed d, thinning positive) beside a frozen connectome diffusion model’s samples over the same ordinal steps. A stage is an ordinal step, never age or time; the measured maps are shown one exact stage at a time. The model was seeded at HY1, fitted at HY2 only and tested at HY3, where it was killed: distance-only and regional-linear predictions beat it.';
/** The registered verdict's scope, verbatim from ba-life (the run's Methods keep it too). */
export const PD_VERDICT_LINE = 'This fixed ordinal cross-sectional atrophy-pattern extrapolator fails; it does not reject protein spread.';

// Expected cortical thickness (lifetime-thickness-v0; docs/contracts/lifetime-thickness-v0.md): ba-life's Rutherford 2022 normative
// reference on fsaverage's Destrieux parcels, the scene's still view on pack fsaverage-destrieux-v1. The app expands a sex's parcel
// medians at an age over the pack's vertices and pushes them through a live vertex layer; two ages subtract vertex by vertex. The
// age is the view's own control, years since birth, 2 to 100: no clock, and nothing maps it to the fetal weeks or to BrainChart's
// post-conception age.

/** Whether the scene's Cortical thickness view is on. */
export const thicknessView = (s: Pick<AtlasState, 'scenario'> & Partial<Pick<AtlasState, 'preset'>>): boolean => s.scenario === 'lifetime' && s.preset === THICKNESS_VIEW;
/** One age's medians, or two ages' difference (B − A). */
export type ThicknessMode = 'age' | 'compare';
/** The layer in the viewport: loading the reference, carrying its data, not on this viewport (no live vertex data), or refused (why). */
export type ThicknessStatus = 'idle' | 'loading' | 'on' | 'none' | 'error';
export const THICKNESS_LAYER = 'lifetime:thickness';
/** The reference's ages, years since birth. */
export const THICKNESS_AGES: [number, number] = [2, 100];
export const DEFAULT_TH_AGE = 30;
export const DEFAULT_TH_AGE_B = 80;
export const DEFAULT_TH_SEX: ThicknessSex = 'Female';
/** A whole year inside the reference's ages; the default for a non-number. */
export const clampAge = (v: number): number => (Number.isFinite(v) ? Math.min(THICKNESS_AGES[1], Math.max(THICKNESS_AGES[0], Math.round(v))) : DEFAULT_TH_AGE);
export const isSex = (v: string | null | undefined): v is ThicknessSex => SEXES.includes(v as ThicknessSex);
/** The difference's scale, mm, centred on zero: the medians fall by a few tenths of a millimetre over adulthood. */
export const THICKNESS_DIFF_RANGE: [number, number] = [-0.6, 0.6];
/** The caption under the viewport in the view: model · data. */
export const thicknessCaption = (mode: ThicknessMode): string =>
  `Normative-model reference · Rutherford 2022 expected cortical thickness, 2–100 years since birth, on fsaverage${mode === 'compare' ? ' · two ages: the difference of published medians' : ''}`;
/** The sheet's paragraph for the view. */
export const THICKNESS_METHODS =
  'Expected cortical thickness on fsaverage’s cortex: Rutherford et al. 2022’s normative model (PCNtoolkit 0.20, a warped Bayesian linear regression fitted to 57K scans from 82 sites), its predictive median per Destrieux parcel at each age from 2 to 100 years since birth, for each sex, with every site indicator at zero (the authors’ plotting convention, not an empirical site average). The parcel medians are expanded over the pack’s vertices exactly, 148 parcels with no transfer or registration; vertices outside the parcellation keep the tissue’s own colour. Between two annual medians the value is interpolated linearly, a derived display operation. Two ages show the difference of published normative medians, not individual change or a forecast. A normative-model reference: a population expectation, not anyone here, with no validation on a new cohort.';
export const THICKNESS_DIFF_LINE = 'The difference of published normative medians, not individual change or a forecast.';
/** The layer's look: the reference's own colormap over its range for one age; a diverging scale centred on zero for a difference. */
export function thicknessLook(sim: Pick<ThicknessSim, 'range' | 'header'>, mode: ThicknessMode): Record<string, unknown> {
  return mode === 'compare' ? { colormap: 'coolwarm', range: THICKNESS_DIFF_RANGE, centre: 0 } : { colormap: sim.header.display?.colormap ?? 'viridis', range: sim.range, rise: [-1, 1] };
}

/** Which frame a track shows at a stage: its index in the track, its own step, and the stage it belongs to: a whole step's
 *  own label (HY3), or "between HY3 and HY4/5" for a model sample off the measured stages. */
export function pdFrameAt(sim: RegionsSim, track: PdTrack, stage: number): { index: number; step: number; label: string } {
  const t = track === 'measured' ? sim.measured : sim.model;
  const index = t.nearest(stage);
  const step = t.steps[index]!;
  return { index, step, label: stageLabelAt(sim, step) };
}

/** The measured stage a step is at, or the two it lies between. */
export function stageLabelAt(sim: RegionsSim, step: number): string {
  const steps = sim.measured.steps;
  const labels = sim.stageLabels;
  const exact = steps.findIndex((s) => Math.abs(s - step) < 1e-6);
  if (exact >= 0) return labels[exact] ?? `stage ${step}`;
  let below = -1;
  for (let i = 0; i < steps.length; i++) if (steps[i]! < step) below = i;
  if (below < 0) return labels[0] ?? `stage ${step}`;
  if (below >= steps.length - 1) return labels[steps.length - 1] ?? `stage ${step}`;
  return `between ${labels[below]} and ${labels[below + 1]}`;
}

/** The overlay for a track at a stage: one frame, keyed by the run's structure ids, over the shared signed range; NaN keeps a
 *  structure's base colour (no data). The measured track is never interpolated; the model shows its nearest sample. */
export function pdOverlay(sim: RegionsSim, track: PdTrack, stage: number): Extract<Command, { cmd: 'overlay'; keys: string[] }> {
  const t = track === 'measured' ? sim.measured : sim.model;
  return { cmd: 'overlay', id: PD_OVERLAY, keys: sim.structureIds, times: [0], values: t.frame(t.nearest(stage)), colormap: 'coolwarm', range: sim.range };
}

/** Spearman's rank correlation of paired values (ties by mean rank); null under three pairs. */
export function spearman(a: ArrayLike<number>, b: ArrayLike<number>): number | null {
  const pairs: [number, number][] = [];
  for (let i = 0; i < Math.min(a.length, b.length); i++) if (Number.isFinite(a[i]) && Number.isFinite(b[i])) pairs.push([a[i]!, b[i]!]);
  if (pairs.length < 3) return null;
  const rank = (xs: number[]): number[] => {
    const order = xs.map((v, i) => [v, i] as const).sort((p, q) => p[0] - q[0]);
    const r = new Array<number>(xs.length);
    for (let i = 0; i < order.length; ) {
      let j = i;
      while (j + 1 < order.length && order[j + 1]![0] === order[i]![0]) j++;
      const mean = (i + j) / 2 + 1;
      for (let k = i; k <= j; k++) r[order[k]![1]] = mean;
      i = j + 1;
    }
    return r;
  };
  const ra = rank(pairs.map((p) => p[0]));
  const rb = rank(pairs.map((p) => p[1]));
  const n = ra.length;
  const ma = ra.reduce((s, v) => s + v, 0) / n;
  const mb = rb.reduce((s, v) => s + v, 0) / n;
  let num = 0;
  let da = 0;
  let db = 0;
  for (let i = 0; i < n; i++) {
    num += (ra[i]! - ma) * (rb[i]! - mb);
    da += (ra[i]! - ma) ** 2;
    db += (rb[i]! - mb) ** 2;
  }
  return da > 0 && db > 0 ? num / Math.sqrt(da * db) : null;
}

// BrainChart centiles (the strip's Centiles tab; docs: #2 comment 5971918792). Chart-only: the run is read here, never sent to the
// viewport. The chart's age is post-conception days shown as years (÷ 365.25); the scene's clock is gestational weeks of another
// sequence, so the chart carries no cursor and converts nothing.

/** What the strip charts: the fetal sequence's own measures (`auto`: area, volume or the section), or the centiles. */
export type Chart = 'auto' | 'centiles';
export const DEFAULT_METRIC: CentileMetric = 'GMV';
export const isMetric = (v: string | null | undefined): v is CentileMetric => (METRICS as string[]).includes(v ?? '');
/** The link's `metric=` value and back: `gmv`, `wmv`, `ventricles`. */
export const metricParam = (m: CentileMetric): string => m.toLowerCase();
export const metricFromParam = (v: string | null): CentileMetric | null => METRICS.find((m) => m.toLowerCase() === (v ?? '').toLowerCase()) ?? null;
export type CentilesStatus = 'idle' | 'loading' | 'ready' | 'error';
/** The caption's addition while the chart is on. */
export const CENTILES_CAPTION = ' · chart: BrainChart centiles by post-conception age, Bethlehem 2022';
/** The two sexes' colours in the strip. */
export const SEX_COLOURS: Record<CentileSex, string> = { Female: SERIES[3]!, Male: SERIES[0]! };

let centilesOpening: Promise<void> | null = null;
/** Reads the centiles once, on first use. */
export async function openCentiles(): Promise<void> {
  if (useLifetime.getState().centiles) return;
  if (centilesOpening) return centilesOpening;
  useLifetime.setState({ centilesStatus: 'loading', centilesWhy: null });
  centilesOpening = (async () => {
    try {
      const sim = await CentilesSim.open(new SimFiles(defaultSource(), LIFETIME_CENTILES));
      useLifetime.setState({ centiles: sim, centilesStatus: 'ready', centilesWhy: null });
    } catch (e) {
      useLifetime.setState({ centilesStatus: 'error', centilesWhy: reason(e) });
    } finally {
      centilesOpening = null;
    }
  })();
  return centilesOpening;
}

/** The strip's chart of one metric: each sex's 2.5th–97.5th band and its median over post-conception years on a log axis. */
export function centilesModel(sim: CentilesSim, metric: CentileMetric): ChartModel | null {
  const series: Series[] = [];
  let hi = 0;
  for (const sex of ['Female', 'Male'] as CentileSex[]) {
    const s = sim.of(metric, sex);
    if (!s) continue;
    const x = Float64Array.from(s.age_post_conception_days, ageYears);
    const lo = Float64Array.from(s.centiles_ml['025']);
    const md = Float64Array.from(s.centiles_ml['500']);
    const up = Float64Array.from(s.centiles_ml['975']);
    for (const v of up) hi = Math.max(hi, v);
    const colour = SEX_COLOURS[sex];
    series.push({ label: `${sex} 2.5th–97.5th`, colour, x, y: up, base: lo, style: 'area', alpha: 0.2, readout: false, unit: 'mL' });
    series.push({ label: `${sex} median`, colour, x, y: md, style: 'line', width: 1.6, unit: 'mL' });
  }
  if (!series.length) return null;
  const [d0, d1] = sim.ageRange;
  const domain: [number, number] = [ageYears(d0), ageYears(d1)];
  const ticks = [0.5, 1, 2, 5, 10, 20, 50, 100].filter((t) => t >= domain[0] && t <= domain[1]);
  // The lane's title carries the axis: an x title sits at the axis's right end and hides the ticks under itself (`drawAxis`),
  // which here are the 20, 50 and 100-year marks, adulthood. The series carry the unit for the readout.
  return {
    x: { domain, log: true, ticks, format: (v) => (v < 1 ? v.toFixed(1) : `${Math.round(v)}`) },
    lanes: [{ label: `${METRIC_NAMES[metric]} in mL by post-conception age in years (days ÷ 365.25)`, y: [0, hi > 0 ? hi * 1.08 : 1], ticks: 'auto', series }],
    gutter: 46,
    readout: 'all',
  };
}

export interface LifetimeStore {
  map: MapId;
  track: Track;
  /** The sequence in the viewport: loading, moving the surfaces, off, a viewport without the layer, or refused (why). */
  status: LifetimeStatus;
  why: string | null;
  /** What the viewport said of the sequence when it took it, if anything. */
  loaded: Record<string, unknown> | null;
  setMap(m: MapId): void;
  setTrack(t: Track): void;
  /** Assigned thickness: the imposed growth shown (the track's own axis), the thickness picked (one of the four, never between), and
   *  what shows past a track's accepted range. */
  growth: number;
  thickness: number;
  beyond: Beyond;
  setGrowth(v: number): void;
  setThickness(mm: number): void;
  setBeyond(b: Beyond): void;
  /** Parkinson's stages: the run once read, the track and stage shown, and whether the viewport has the overlay. */
  pd: RegionsSim | null;
  pdTrack: PdTrack;
  stage: number;
  pdStatus: PdStatus;
  pdWhy: string | null;
  /** How many overlay frames the viewport has taken, for probes. */
  pdSent: number;
  setPdTrack(t: PdTrack): void;
  setStage(v: number): void;
  /** Cortical thickness: the reference once read, the mode, ages and sex shown, and whether the viewport carries the layer. */
  th: ThicknessSim | null;
  thMode: ThicknessMode;
  thAge: number;
  thAgeB: number;
  thSex: ThicknessSex;
  thStatus: ThicknessStatus;
  thWhy: string | null;
  /** What the viewport said of the layer: the data's vertex count, and the drawn vertices it reaches of those it's on. */
  thReply: { vertices: number; covered: number; of: number } | null;
  /** How many frames of data the viewport has taken, for probes. */
  thSent: number;
  setThMode(m: ThicknessMode): void;
  setThAge(v: number): void;
  setThAgeB(v: number): void;
  setThSex(s: ThicknessSex): void;
  /** The strip's chart, the metric shown, and the centiles once read. */
  chart: Chart;
  metric: CentileMetric;
  centiles: CentilesSim | null;
  centilesStatus: CentilesStatus;
  centilesWhy: string | null;
  setChart(c: Chart): void;
  setMetric(m: CentileMetric): void;
}

const INITIAL = {
  map: DEFAULT_MAP,
  track: DEFAULT_TRACK,
  growth: DEFAULT_GROWTH,
  thickness: DEFAULT_THICKNESS,
  beyond: DEFAULT_BEYOND,
  status: 'idle' as LifetimeStatus,
  why: null,
  loaded: null,
  pd: null,
  pdTrack: DEFAULT_PD_TRACK,
  stage: DEFAULT_STAGE,
  pdStatus: 'idle' as PdStatus,
  pdWhy: null,
  pdSent: 0,
  th: null,
  thMode: 'age' as ThicknessMode,
  thAge: DEFAULT_TH_AGE,
  thAgeB: DEFAULT_TH_AGE_B,
  thSex: DEFAULT_TH_SEX,
  thStatus: 'idle' as ThicknessStatus,
  thWhy: null,
  thReply: null,
  thSent: 0,
  chart: 'auto' as Chart,
  metric: DEFAULT_METRIC,
  centiles: null,
  centilesStatus: 'idle' as CentilesStatus,
  centilesWhy: null,
};

export const useLifetime = create<LifetimeStore>()((set) => ({
  ...INITIAL,
  setMap: (map) => set({ map }),
  setTrack: (track) => set({ track }),
  setGrowth: (v) => set({ growth: clampGrowth(v) }),
  setThickness: (mm) => set({ thickness: Number.isFinite(mm) && mm > 0 ? mm : DEFAULT_THICKNESS }),
  setBeyond: (beyond) => set({ beyond }),
  setPdTrack: (pdTrack) => set({ pdTrack }),
  setStage: (v) => set({ stage: clampStage(v) }),
  setThMode: (thMode) => set({ thMode }),
  setThAge: (v) => set({ thAge: clampAge(v) }),
  setThAgeB: (v) => set({ thAgeB: clampAge(v) }),
  setThSex: (thSex) => set({ thSex }),
  setChart: (chart) => set({ chart }),
  setMetric: (metric) => set({ metric }),
}));

const reason = (e: unknown): string => (e instanceof Error ? e.message : String(e));

/** The layer command for a map: the surfaces moving, lit by the map over its scale, or plain. */
export function morphLayer(map: SurfaceMap): Command {
  return {
    cmd: 'layer',
    name: 'morph',
    on: true,
    slot: LIFETIME_SLOT,
    ...(map.quantity && map.range ? { quantity: map.quantity, range: map.range, ...(map.colormap ? { colormap: map.colormap } : {}) } : {}),
  };
}

// One drive at a time: a start or stop that a later one overtakes gives up at its next step.
let gen = 0;

/** The map the layer is lit by now: the panel's, where the sequence in the main slot publishes it. */
function litBy(): SurfaceMap {
  const o = openedSlot('main');
  return litMap(mapOf(useLifetime.getState().map), o?.format === 'lifetime' ? o.sim : null);
}

async function start(path: string): Promise<void> {
  const my = ++gen;
  useLifetime.setState({ status: 'loading', why: null });
  const vp = await getViewport();
  if (!vp || my !== gen) return;
  try {
    packBoundSlots.add(LIFETIME_SLOT);
    const r = (await vp.send({ cmd: 'sim', path, slot: LIFETIME_SLOT })) as Record<string, unknown> | undefined;
    if (my !== gen) return;
    await vp.send(morphLayer(litBy()));
    if (my !== gen) return;
    useLifetime.setState({ status: 'on', why: null, loaded: r && typeof r === 'object' ? r : null });
  } catch (e) {
    if (my !== gen) return;
    useLifetime.setState({ status: 'error', why: reason(e) });
  }
}

async function sendLayer(my = gen): Promise<void> {
  const vp = await getViewport();
  if (!vp || my !== gen) return;
  try {
    await vp.send(morphLayer(litBy()));
  } catch (e) {
    if (my !== gen) return;
    useLifetime.setState({ status: 'error', why: reason(e) });
  }
}

async function stop(): Promise<void> {
  const my = ++gen;
  const was = useLifetime.getState().status;
  useLifetime.setState({ status: 'off', why: null });
  if (was !== 'on' && was !== 'loading') return void packBoundSlots.delete(LIFETIME_SLOT);
  const vp = await getViewport();
  if (!vp || my !== gen) return;
  try {
    await vp.send({ cmd: 'layer', name: 'morph', on: false, slot: LIFETIME_SLOT });
    await vp.send({ cmd: 'sim', path: null, slot: LIFETIME_SLOT });
  } catch {
    // A viewport that never took the sequence has nothing to forget.
  }
  // Out of the bridge's pack-bound slots once empty, unless a newer start has the slot.
  if (my === gen) packBoundSlots.delete(LIFETIME_SLOT);
}

/** Whether this viewport moves surfaces: not the 2D preview; `ready.layers` lists `morph` where it says, and `ready.sims` the
 *  format where it says (viewport-api-v0, Changes: an engine without them refuses the sim). */
export const lifetimeSupported = (s: Pick<AtlasState, 'engine'>): boolean =>
  s.engine.kind !== 'mock' && drawsLayer(s.engine.layers, 'morph') && (!s.engine.sims || s.engine.sims.includes('lifetime-sim-v0'));

/** Why the viewport isn't moving the surface, for the panel. */
export function unsupportedWhy(s: Pick<AtlasState, 'engine'>): string {
  return s.engine.kind === 'mock'
    ? 'The 2D preview doesn’t move the surface: the growing cortex needs the 3D viewport. The clock, the chart and the numbers are the atlas’s.'
    : 'This viewport doesn’t move surfaces: its build lists no morph layer. The clock, the chart and the numbers are the atlas’s.';
}

/** Drives the slot while the scene is on and its pack has loaded: the sequence in, its layer following the panel's map; out when
 *  the scene goes or the pack changes (a pack load drops every morph), and aside in the scene's still view (Parkinson's stages,
 *  on another pack). Mounted once, by the panel's sections (Lifetime.tsx). */
export function useLifetimeDrive(): void {
  const active = useAtlas(lifetimeActive);
  const still = useAtlas(stillView);
  const ready = useAtlas((s) => s.engine.status === 'ready' && s.load.status === 'ready' && s.packId === packOf(s));
  const supported = useAtlas(lifetimeSupported);
  // The chosen period's run, once the scene's runs are in: a change of period (another run on another pack) stops the sequence
  // and starts the new one when its pack is loaded.
  const path = useAtlas(sequencePath);
  const map = useLifetime((s) => s.map);
  useEffect(() => {
    if (!active || still || !ready || !path) return;
    if (!supported) {
      useLifetime.setState({ status: 'unsupported', why: unsupportedWhy(useAtlas.getState()) });
      return;
    }
    void start(path);
    return () => void stop();
  }, [active, still, ready, supported, path]);
  useEffect(() => {
    if (active && !still && useLifetime.getState().status === 'on') void sendLayer();
  }, [active, still, map, path]);
}

// The Parkinson's stages view: the run is read once (its header and both tracks, 40 KB), the chosen frame goes as one overlay
// when the template has loaded, follows the track and stage, and is taken away when the view goes.
let pdGen = 0;
let pdOpening: Promise<void> | null = null;

async function openPd(): Promise<void> {
  if (useLifetime.getState().pd) return;
  if (pdOpening) return pdOpening;
  useLifetime.setState({ pdStatus: 'loading', pdWhy: null });
  pdOpening = (async () => {
    try {
      const sim = await RegionsSim.open(new SimFiles(defaultSource(), LIFETIME_PD));
      useLifetime.setState({ pd: sim });
    } catch (e) {
      useLifetime.setState({ pdStatus: 'error', pdWhy: reason(e) });
    } finally {
      pdOpening = null;
    }
  })();
  return pdOpening;
}

async function sendPd(my: number): Promise<void> {
  const { pd, pdTrack, stage } = useLifetime.getState();
  if (!pd) return;
  const vp = await getViewport();
  if (!vp || my !== pdGen) return;
  try {
    await vp.send(pdOverlay(pd, pdTrack, stage));
    if (my !== pdGen) return;
    useLifetime.setState((s) => ({ pdStatus: 'on', pdWhy: null, pdSent: s.pdSent + 1 }));
  } catch (e) {
    if (my === pdGen) useLifetime.setState({ pdStatus: 'error', pdWhy: reason(e) });
  }
}

async function clearPd(): Promise<void> {
  ++pdGen;
  if (useLifetime.getState().pdStatus !== 'error') useLifetime.setState({ pdStatus: 'idle', pdWhy: null });
  const vp = await getViewport();
  try {
    await vp?.send({ cmd: 'overlay', id: PD_OVERLAY, remove: true });
  } catch {
    // A viewport that never took the overlay has nothing to forget.
  }
}

/** Drives the Parkinson's stages overlay while the view is on and the template has loaded. Mounted with the sections. */
export function usePdDrive(): void {
  const on = useAtlas(pdView);
  const ready = useAtlas((s) => s.engine.status === 'ready' && s.load.status === 'ready' && s.packId === packOf(s));
  const sim = useLifetime((s) => s.pd);
  const track = useLifetime((s) => s.pdTrack);
  const stage = useLifetime((s) => s.stage);
  useEffect(() => {
    if (on) void openPd();
  }, [on]);
  useEffect(() => {
    if (!on || !ready || !sim) return;
    const my = ++pdGen;
    void sendPd(my);
    return () => void clearPd();
  }, [on, ready, sim]);
  useEffect(() => {
    if (on && ready && sim && useLifetime.getState().pdStatus === 'on') void sendPd(pdGen);
  }, [on, ready, sim, track, stage]);
}

// The Cortical thickness view: the reference is read once (its header, then the two series and the vertex index, 770 KB), the
// layer is made on the pack's two cortex structures when the pack has loaded, and the chosen age's medians (or two ages'
// difference) are pushed as the layer's data; the layer is taken away when the view goes.
let thGen = 0;
let thOpening: Promise<void> | null = null;

async function openThickness(): Promise<void> {
  if (useLifetime.getState().th) return;
  if (thOpening) return thOpening;
  useLifetime.setState({ thStatus: 'loading', thWhy: null });
  thOpening = (async () => {
    try {
      const sim = await ThicknessSim.open(new SimFiles(defaultSource(), LIFETIME_THICKNESS));
      useLifetime.setState({ th: sim });
    } catch (e) {
      useLifetime.setState({ thStatus: 'error', thWhy: reason(e) });
    } finally {
      thOpening = null;
    }
  })();
  return thOpening;
}

/** The live API (viewport-api-v0, Live vertex data), optional on today's viewports: the 2D preview has no `setVertexData`. */
interface LiveVp {
  send(cmd: Command): Promise<unknown>;
  setVertexData?(id: string, values: Float32Array, t?: number): void;
}

/** Makes the layer for the mode (`remake`: a layer sent again starts over without data) and pushes the values for the age or ages. */
async function sendThickness(my: number, remake: boolean): Promise<void> {
  const { th, thMode, thAge, thAgeB, thSex } = useLifetime.getState();
  if (!th) return;
  const vp = (await getViewport()) as LiveVp | null;
  if (!vp || my !== thGen) return;
  if (typeof vp.setVertexData !== 'function') {
    useLifetime.setState({ thStatus: 'none', thWhy: 'This viewport doesn’t take live vertex data: the 2D preview draws the cortex without the reference.', thReply: null });
    return;
  }
  try {
    if (remake) {
      const reply = (await vp.send({ cmd: 'vertexLayer', id: THICKNESS_LAYER, surface: th.structureIds, space: 'own', kind: 'scalar', look: thicknessLook(th, thMode) } as unknown as Command)) as
        | { vertices?: number; covered?: number; of?: number }
        | undefined;
      if (my !== thGen) return;
      useLifetime.setState({ thReply: reply && typeof reply.covered === 'number' ? { vertices: reply.vertices ?? 0, covered: reply.covered, of: reply.of ?? 0 } : null });
    }
    const values = thMode === 'compare' ? await th.difference(thSex, thAge, thAgeB) : await th.sample(thSex, thAge);
    if (my !== thGen) return;
    vp.setVertexData(THICKNESS_LAYER, values);
    useLifetime.setState((s) => ({ thStatus: 'on', thWhy: null, thSent: s.thSent + 1 }));
  } catch (e) {
    if (my === thGen) useLifetime.setState({ thStatus: 'error', thWhy: reason(e) });
  }
}

async function clearThickness(): Promise<void> {
  ++thGen;
  if (useLifetime.getState().thStatus !== 'error') useLifetime.setState({ thStatus: 'idle', thWhy: null, thReply: null });
  const vp = await getViewport();
  try {
    await vp?.send({ cmd: 'vertexLayer', id: THICKNESS_LAYER, remove: true } as unknown as Command);
  } catch {
    // A viewport that never took the layer has nothing to forget.
  }
}

/** Drives the thickness layer while the view is on and its pack has loaded. Mounted with the sections. */
export function useThicknessDrive(): void {
  const on = useAtlas(thicknessView);
  const ready = useAtlas((s) => s.engine.status === 'ready' && s.load.status === 'ready' && s.packId === packOf(s));
  const sim = useLifetime((s) => s.th);
  const mode = useLifetime((s) => s.thMode);
  const age = useLifetime((s) => s.thAge);
  const ageB = useLifetime((s) => s.thAgeB);
  const sex = useLifetime((s) => s.thSex);
  useEffect(() => {
    if (on) void openThickness();
  }, [on]);
  // The layer for the mode: made when the view, its pack and the reference are in, and again when the mode changes its look.
  useEffect(() => {
    if (!on || !ready || !sim) return;
    const my = ++thGen;
    void sendThickness(my, true);
    return () => void clearThickness();
  }, [on, ready, sim, mode]);
  useEffect(() => {
    if (on && ready && sim && useLifetime.getState().thStatus === 'on') void sendThickness(thGen, false);
  }, [on, ready, sim, age, ageB, sex]);
}

// The store for probes and the e2e run, as cfd.ts and dfc.ts expose theirs.
if (typeof window !== 'undefined') (window as unknown as { __lifetime?: typeof useLifetime }).__lifetime = useLifetime;
